/**
 * viewportUI.js — Enterprise Viewport UI Overlays
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * [v2.0 — ULTIMATE UI SUITE]:
 *  - showRulerGrid: правильный pxPerMm через camera.parallelScale
 *  - Интервалы привязаны к жизненному циклу (проверка element.isConnected)
 *  - Лупа: адаптивный размер при ресайзе viewport'а
 *  - DICOM Overlay (отображение ROI/измерений из DICOM-тегов)
 *  - Cinematic letterbox (чёрные полосы)
 *  - Progress indicator (круговой индикатор загрузки)
 *  - Measurement tooltips (всплывающие подсказки)
 *  - Viewport colorbar (цветовая шкала HU)
 *  - Patient orientation cube (3D-кубик)
 *  - Quick actions radial menu (круговое меню)
 *
 * @module viewportUI
 */

import state, { getRenderingEngine, getViewportIds, getActiveOrFirstViewportId, ViewportType, cornerstone, cornerstoneTools } from './viewportState.js';
import { screenshotViewport } from './viewportScreenshot.js';
import { events } from './events.js';
import { EVENTS } from './eventBus.js';
import { showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';

// Кэшированный ленивый импорт
let _corePromise = null;
const _core = () => {
  if (!_corePromise) _corePromise = import('./viewport.js');
  return _corePromise;
};

// ══════════════════════════════════════════════════════════════
//  SAFE INTERVAL (привязан к жизненному циклу элемента)
// ══════════════════════════════════════════════════════════════

function _safeInterval(callback, ms, element) {
  const id = setInterval(() => {
    if (!element?.isConnected) {
      clearInterval(id);
      return;
    }
    try { callback(); } catch (e) { log.debug("[viewportUI]", e.message); }
  }, ms);
  return id;
}

// ══════════════════════════════════════════════════════════════
//  MAGNIFYING GLASS (адаптивный размер)
// ══════════════════════════════════════════════════════════════

const _magnifiers = new Map();

export function toggleMagnifier(viewportId, opts = {}) {
  const vpId = viewportId || getActiveOrFirstViewportId();
  _magnifiers.has(vpId) ? disableMagnifier(vpId) : enableMagnifier(vpId, opts);
}

export function enableMagnifier(viewportId, opts = {}) {
  const vpId = viewportId || getActiveOrFirstViewportId();
  if (!vpId || _magnifiers.has(vpId)) return;
  const vp = getRenderingEngine()?.getViewport(vpId);
  const el = vp?.element;
  if (!el) return;

  const zoom = opts.zoom || 3;
  const baseSize = opts.size || 160;
  const showHU = opts.showHU !== false;
  const half = baseSize / 2;

  const loupe = document.createElement('canvas');
  loupe.className = 'vp-magnifier';
  loupe.width = baseSize;
  loupe.height = baseSize + (showHU ? 24 : 0);
  loupe.style.cssText = 'position:absolute;pointer-events:none;display:none;border:2px solid rgba(255,255,255,0.7);border-radius:50%;box-shadow:0 0 10px rgba(0,0,0,0.5);z-index:100;image-rendering:pixelated;';
  if (showHU) loupe.style.borderRadius = `${half}px ${half}px 12px 12px`;
  el.appendChild(loupe);
  const ctx = loupe.getContext('2d');
  const src = vp.canvas || el.querySelector('canvas');

  const handler = (evt) => {
    if (!src || !el.isConnected) return;
    const rect = el.getBoundingClientRect();
    const x = (evt.clientX || evt.touches?.[0]?.clientX || 0) - rect.left;
    const y = (evt.clientY || evt.touches?.[0]?.clientY || 0) - rect.top;
    loupe.style.display = 'block';
    loupe.style.left = `${Math.min(x + 20, rect.width - baseSize - 5)}px`;
    loupe.style.top = `${Math.max(y - loupe.height - 20, 5)}px`;
    const sx = x * src.width / rect.width;
    const sy = y * src.height / rect.height;
    const sr = (half / zoom) * src.width / rect.width;
    ctx.clearRect(0, 0, loupe.width, loupe.height);

    // Circle clip
    ctx.save();
    ctx.beginPath();
    ctx.arc(half, half, half - 1, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(src, sx - sr, sy - sr, sr * 2, sr * 2, 0, 0, baseSize, baseSize);
    // Crosshair
    ctx.strokeStyle = 'rgba(255,200,0,0.7)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(half - 10, half); ctx.lineTo(half + 10, half);
    ctx.moveTo(half, half - 10); ctx.lineTo(half, half + 10);
    ctx.stroke();
    ctx.restore();

    // HU readout
    if (showHU) {
      try {
        const worldPos = vp.canvasToWorld([x, y]);
        let hu = null;
        if (vp.type === ViewportType.ORTHOGRAPHIC) {
          const imageData = vp.getImageData?.();
          if (imageData) {
            const dims = imageData.getDimensions?.() || imageData.dimensions;
            const idx = imageData.worldToIndex(worldPos);
            if (dims && idx) {
              const [ix, iy, iz] = [Math.round(idx[0]), Math.round(idx[1]), Math.round(idx[2])];
              if (ix >= 0 && ix < dims[0] && iy >= 0 && iy < dims[1] && iz >= 0 && iz < dims[2]) {
                const sd = imageData.getPointData?.()?.getScalars?.()?.getData?.();
                if (sd) hu = Math.round(sd[ix + iy * dims[0] + iz * dims[0] * dims[1]]);
              }
            }
          }
        }
        ctx.fillStyle = 'rgba(0,0,0,0.75)';
        ctx.fillRect(0, baseSize, baseSize, 24);
        ctx.fillStyle = '#60a5fa';
        ctx.font = 'bold 13px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(hu !== null ? `${hu} HU` : '— HU', half, baseSize + 17);
      } catch (e) { log.debug("[viewportUI]", e.message); }
    }
  };

  const hideHandler = () => { loupe.style.display = 'none'; };

  el.addEventListener('mousemove', handler);
  el.addEventListener('touchmove', handler, { passive: true });
  el.addEventListener('mouseleave', hideHandler);

  _magnifiers.set(vpId, { canvas: loupe, handler, hideHandler, el });
  showToast(`🔍 Лупа ×${zoom}`, 'info', 1200);
}

export function disableMagnifier(viewportId) {
  const e = _magnifiers.get(viewportId);
  if (!e) return;
  e.el.removeEventListener('mousemove', e.handler);
  e.el.removeEventListener('touchmove', e.handler);
  e.el.removeEventListener('mouseleave', e.hideHandler);
  try { e.canvas.remove(); } catch {}
  _magnifiers.delete(viewportId);
}

// ══════════════════════════════════════════════════════════════
//  CROSSHAIR OVERLAY
// ══════════════════════════════════════════════════════════════

const _crosshairs = new Map();

export function showCrosshairOverlay(viewportId, opts = {}) {
  hideCrosshairOverlay(viewportId);
  const vp = getRenderingEngine()?.getViewport(viewportId);
  const el = vp?.element;
  if (!el) return;

  const color = opts.color || 'rgba(0,200,255,0.5)';
  const thickness = opts.thickness || 1;

  const canvas = document.createElement('canvas');
  canvas.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:45;';
  el.appendChild(canvas);

  const draw = () => {
    if (!el.isConnected) return;
    const w = el.clientWidth, h = el.clientHeight;
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, w, h);
    ctx.strokeStyle = color; ctx.lineWidth = thickness;
    ctx.setLineDash([8, 4]);
    ctx.beginPath();
    ctx.moveTo(w / 2, 0); ctx.lineTo(w / 2, h);
    ctx.moveTo(0, h / 2); ctx.lineTo(w, h / 2);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.beginPath(); ctx.arc(w / 2, h / 2, 6, 0, Math.PI * 2);
    ctx.stroke();
  };
  draw();
  const ro = new ResizeObserver(() => requestAnimationFrame(draw));
  ro.observe(el);
  _crosshairs.set(viewportId, { canvas, ro, el });
}

export function hideCrosshairOverlay(viewportId) {
  const e = _crosshairs.get(viewportId);
  if (!e) return;
  e.ro.disconnect();
  try { e.canvas.remove(); } catch {}
  _crosshairs.delete(viewportId);
}

export function toggleCrosshairOverlay(viewportId, opts) {
  _crosshairs.has(viewportId) ? hideCrosshairOverlay(viewportId) : showCrosshairOverlay(viewportId, opts);
}

// ══════════════════════════════════════════════════════════════
//  RULER GRID + SCALE BAR (исправлен pxPerMm для ORTHOGRAPHIC)
// ══════════════════════════════════════════════════════════════

const _rulerGrids = new Map();

export function toggleRulerGrid(viewportId, opts) {
  _rulerGrids.has(viewportId) ? removeRulerGrid(viewportId) : showRulerGrid(viewportId, opts);
}

export function showRulerGrid(viewportId, opts = {}) {
  const vpId = viewportId || getActiveOrFirstViewportId();
  if (!vpId || _rulerGrids.has(vpId)) return;
  const vp = getRenderingEngine()?.getViewport(vpId);
  const el = vp?.element;
  if (!el) return;

  const stepMm = opts.stepMm || 10;
  const color = opts.color || 'rgba(255,255,0,0.2)';
  const overlay = document.createElement('canvas');
  overlay.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:50;';
  el.appendChild(overlay);

  const draw = () => {
    if (!el.isConnected) return;
    const w = el.clientWidth, h = el.clientHeight;
    overlay.width = w; overlay.height = h;
    const ctx = overlay.getContext('2d');
    ctx.clearRect(0, 0, w, h);

    let pxPerMm = 1;
    try {
      if (vp.type === ViewportType.ORTHOGRAPHIC) {
        const cam = vp.getCamera?.();
        if (cam?.parallelScale) {
          pxPerMm = (h / 2) / cam.parallelScale;
        } else {
          const sp = vp.getImageData?.()?.getSpacing?.() || [1];
          pxPerMm = (vp.getZoom?.() || 1) / sp[0];
        }
      } else if (vp.type === ViewportType.STACK) {
        const img = cornerstone.cache.getImage?.(vp.getCurrentImageId?.());
        pxPerMm = (vp.getZoom?.() || 1) / (img?.columnPixelSpacing || 1);
      }
    } catch (e) { log.debug("[viewportUI]", e.message); }

    const stepPx = stepMm * pxPerMm;
    if (stepPx < 5) return;

    ctx.strokeStyle = color;
    ctx.lineWidth = 0.5;
    ctx.font = '10px monospace';
    ctx.fillStyle = 'rgba(255,255,0,0.5)';
    for (let x = stepPx; x < w; x += stepPx) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke(); }
    for (let y = stepPx; y < h; y += stepPx) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke(); }

    // Scale bar (50mm)
    const barPx = 50 * pxPerMm;
    if (barPx > 20 && barPx < w * 0.8) {
      ctx.strokeStyle = 'rgba(255,255,0,0.8)';
      ctx.lineWidth = 2;
      const bx = w - barPx - 15, by = h - 20;
      ctx.beginPath();
      ctx.moveTo(bx, by); ctx.lineTo(bx + barPx, by);
      ctx.moveTo(bx, by - 5); ctx.lineTo(bx, by + 5);
      ctx.moveTo(bx + barPx, by - 5); ctx.lineTo(bx + barPx, by + 5);
      ctx.stroke();
      ctx.fillStyle = 'rgba(255,255,0,0.8)';
      ctx.font = '12px monospace';
      ctx.fillText('50 mm', bx + barPx / 2 - 18, by - 6);
    }
  };
  draw();
  const ro = new ResizeObserver(() => requestAnimationFrame(draw));
  ro.observe(el);
  _rulerGrids.set(vpId, { canvas: overlay, ro, el });
  showToast('📏 Масштабная сетка', 'info', 1200);
}

export function removeRulerGrid(viewportId) {
  const e = _rulerGrids.get(viewportId);
  if (!e) return;
  e.ro.disconnect();
  try { e.canvas.remove(); } catch {}
  _rulerGrids.delete(viewportId);
}

// ══════════════════════════════════════════════════════════════
//  INFO OVERLAY (с безопасным интервалом)
// ══════════════════════════════════════════════════════════════

const _infoOverlays = new Map();

export function showInfoOverlay(viewportId, info = {}) {
  hideInfoOverlay(viewportId);
  const vp = getRenderingEngine()?.getViewport(viewportId);
  if (!vp?.element) return;
  const el = vp.element;
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:absolute;top:0;left:0;right:0;bottom:0;pointer-events:none;z-index:60;padding:6px 8px;font:12px/1.4 monospace;color:rgba(255,255,255,0.85);text-shadow:1px 1px 2px rgba(0,0,0,0.8);';

  const mkC = (s) => { const d = document.createElement('div'); d.style.cssText = `position:absolute;${s};white-space:pre;padding:4px 6px;`; return d; };
  const c = {
    tl: mkC('top:0;left:0'),
    tr: mkC('top:0;right:0;text-align:right'),
    bl: mkC('bottom:0;left:0'),
    br: mkC('bottom:0;right:0;text-align:right'),
  };
  Object.values(c).forEach(v => overlay.appendChild(v));
  el.appendChild(overlay);

  let fc = 0, lt = performance.now(), fps = 0;
  const update = () => {
    try {
      if (!el.isConnected) return;
      fc++;
      const now = performance.now();
      if (now - lt >= 1000) { fps = fc; fc = 0; lt = now; }
      const cam = vp.getCamera?.() || {}, props = vp.getProperties?.() || {};
      c.tl.textContent = [info.patientName, info.studyDate, info.institution].filter(Boolean).join('\n');
      c.tr.textContent = [info.seriesDesc, info.modality].filter(Boolean).join('\n');
      const bl = [];
      if (vp.type === ViewportType.STACK) bl.push(`Img: ${(vp.getCurrentImageIdIndex?.() ?? 0) + 1}/${vp.getImageIds?.()?.length ?? 0}`);
      if (cam.focalPoint) bl.push(`Pos: ${cam.focalPoint.map(v => v?.toFixed?.(1) ?? '?').join(', ')}`);
      const voi = props.voiRange;
      if (voi) bl.push(`W/L: ${Math.round(voi.upper - voi.lower)}/${Math.round((voi.upper + voi.lower) / 2)}`);
      c.bl.textContent = bl.join('\n');
      c.br.textContent = `${fps} fps`;
    } catch (e) { log.debug("[viewportUI]", e.message); }
  };
  const intervalId = _safeInterval(update, 250, el);
  update();
  _infoOverlays.set(viewportId, { div: overlay, intervalId, el });
}

export function hideInfoOverlay(viewportId) {
  const e = _infoOverlays.get(viewportId);
  if (!e) return;
  clearInterval(e.intervalId);
  try { e.div.remove(); } catch {}
  _infoOverlays.delete(viewportId);
}

// ══════════════════════════════════════════════════════════════
//  PICTURE-IN-PICTURE (безопасный интервал)
// ══════════════════════════════════════════════════════════════

const _pipOverlays = new Map();

export function showPictureInPicture(hostViewportId, sourceViewportId, opts = {}) {
  hidePictureInPicture(hostViewportId);
  const re = getRenderingEngine();
  const hostVp = re?.getViewport(hostViewportId);
  const srcVp = re?.getViewport(sourceViewportId);
  if (!hostVp?.element || !srcVp) return;

  const size = opts.size || 120;
  const corner = opts.corner || 'tr';
  const el = hostVp.element;

  const pip = document.createElement('canvas');
  pip.width = size; pip.height = size;
  const posStyle = { tr: 'top:8px;right:8px', tl: 'top:8px;left:8px', br: 'bottom:8px;right:8px', bl: 'bottom:8px;left:8px' }[corner] || 'top:8px;right:8px';
  pip.style.cssText = `position:absolute;${posStyle};z-index:80;border:1px solid rgba(255,255,255,0.4);border-radius:4px;pointer-events:none;box-shadow:0 2px 8px rgba(0,0,0,0.5);`;
  el.appendChild(pip);
  const ctx = pip.getContext('2d');

  const update = () => {
    try {
      if (!el.isConnected) return;
      const srcCanvas = srcVp.canvas || srcVp.element?.querySelector('canvas');
      if (srcCanvas) {
        ctx.clearRect(0, 0, size, size);
        ctx.drawImage(srcCanvas, 0, 0, size, size);
      }
    } catch (e) { log.debug("[viewportUI]", e.message); }
  };
  const intervalId = _safeInterval(update, 500, el);
  update();
  _pipOverlays.set(hostViewportId, { canvas: pip, intervalId, el });
}

export function hidePictureInPicture(viewportId) {
  const e = _pipOverlays.get(viewportId);
  if (!e) return;
  clearInterval(e.intervalId);
  try { e.canvas.remove(); } catch {}
  _pipOverlays.delete(viewportId);
}

// ══════════════════════════════════════════════════════════════
//  CINEMATIC LETTERBOX
// ══════════════════════════════════════════════════════════════

const _letterboxes = new Map();

export function showLetterbox(viewportId, ratio = 2.35) {
  hideLetterbox(viewportId);
  const vp = getRenderingEngine()?.getViewport(viewportId);
  const el = vp?.element;
  if (!el) return;

  const top = document.createElement('div');
  top.style.cssText = 'position:absolute;top:0;left:0;right:0;background:#000;z-index:90;pointer-events:none;transition:height 0.5s;';
  const bottom = document.createElement('div');
  bottom.style.cssText = 'position:absolute;bottom:0;left:0;right:0;background:#000;z-index:90;pointer-events:none;transition:height 0.5s;';
  el.appendChild(top);
  el.appendChild(bottom);

  const update = () => {
    if (!el.isConnected) return;
    const h = el.clientHeight;
    const barH = Math.round(h * (1 - 1 / ratio) / 2);
    top.style.height = `${barH}px`;
    bottom.style.height = `${barH}px`;
  };
  update();
  const ro = new ResizeObserver(() => requestAnimationFrame(update));
  ro.observe(el);
  _letterboxes.set(viewportId, { top, bottom, ro, el });
}

export function hideLetterbox(viewportId) {
  const e = _letterboxes.get(viewportId);
  if (!e) return;
  e.ro.disconnect();
  try { e.top.remove(); e.bottom.remove(); } catch {}
  _letterboxes.delete(viewportId);
}

// ══════════════════════════════════════════════════════════════
//  PROGRESS INDICATOR
// ══════════════════════════════════════════════════════════════

const _progressIndicators = new Map();

export function showProgressIndicator(viewportId, progress = 0) {
  let pi = _progressIndicators.get(viewportId);
  if (!pi) {
    const vp = getRenderingEngine()?.getViewport(viewportId);
    const el = vp?.element;
    if (!el) return;

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 36 36');
    svg.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:48px;height:48px;z-index:200;pointer-events:none;';
    svg.innerHTML = `
      <circle cx="18" cy="18" r="15" fill="none" stroke="rgba(255,255,255,0.15)" stroke-width="3"/>
      <circle cx="18" cy="18" r="15" fill="none" stroke="#0af" stroke-width="3" stroke-dasharray="94.2" stroke-dashoffset="94.2" stroke-linecap="round" transform="rotate(-90 18 18)" id="pi-arc"/>
      <text x="18" y="22" text-anchor="middle" fill="#fff" font-size="8" id="pi-text">0%</text>
    `;
    el.appendChild(svg);
    pi = { svg, el };
    _progressIndicators.set(viewportId, pi);
  }

  const pct = Math.max(0, Math.min(100, progress));
  const arc = pi.svg.querySelector('#pi-arc');
  const text = pi.svg.querySelector('#pi-text');
  if (arc) arc.setAttribute('stroke-dashoffset', (94.2 * (1 - pct / 100)).toFixed(1));
  if (text) text.textContent = `${Math.round(pct)}%`;
}

export function hideProgressIndicator(viewportId) {
  const pi = _progressIndicators.get(viewportId);
  if (!pi) return;
  try { pi.svg.remove(); } catch {}
  _progressIndicators.delete(viewportId);
}

// ══════════════════════════════════════════════════════════════
//  MEASUREMENT TOOLTIPS
// ══════════════════════════════════════════════════════════════

const _tooltips = new Map();

export function enableMeasurementTooltips(viewportId) {
  const vp = getRenderingEngine()?.getViewport(viewportId);
  const el = vp?.element;
  if (!el || _tooltips.has(viewportId)) return;

  const tooltip = document.createElement('div');
  tooltip.style.cssText = 'position:absolute;pointer-events:none;display:none;background:rgba(0,0,0,0.8);color:#0af;padding:2px 6px;border-radius:3px;font:11px monospace;z-index:150;white-space:nowrap;';
  el.appendChild(tooltip);

  const handler = (evt) => {
    if (!el.isConnected) return;
    const rect = el.getBoundingClientRect();
    const x = evt.clientX - rect.left;
    const y = evt.clientY - rect.top;

    // Ищем аннотации рядом с курсором
    try {
      const allAnns = cornerstoneTools?.annotation?.state?.getAllAnnotations?.() || [];
      const vpAnns = allAnns.filter(a => a.metadata?.viewportId === viewportId);
      let found = null, bestDist = 30; // радиус захвата 30px

      for (const ann of vpAnns) {
        const handles = ann.data?.handles?.points || [];
        for (const pt of handles) {
          const canvasPt = vp.worldToCanvas(pt);
          if (canvasPt) {
            const dist = Math.hypot(canvasPt[0] - x, canvasPt[1] - y);
            if (dist < bestDist) {
              bestDist = dist;
              found = { label: ann.metadata?.toolName || ann.type, value: ann.data?.cachedStats?.length || ann.data?.cachedStats?.area || '' };
            }
          }
        }
      }

      if (found) {
        tooltip.style.display = 'block';
        tooltip.style.left = `${x + 15}px`;
        tooltip.style.top = `${y - 15}px`;
        tooltip.textContent = `${found.label}: ${typeof found.value === 'number' ? found.value.toFixed(1) + ' mm' : found.value}`;
      } else {
        tooltip.style.display = 'none';
      }
    } catch (e) { tooltip.style.display = 'none'; }
  };

  el.addEventListener('mousemove', handler);
  _tooltips.set(viewportId, { tooltip, handler, el });
}

export function disableMeasurementTooltips(viewportId) {
  const e = _tooltips.get(viewportId);
  if (!e) return;
  e.el.removeEventListener('mousemove', e.handler);
  try { e.tooltip.remove(); } catch {}
  _tooltips.delete(viewportId);
}

// ══════════════════════════════════════════════════════════════
//  VIEWPORT COLORBAR (шкала HU)
// ══════════════════════════════════════════════════════════════

const _colorbars = new Map();

export function showColorbar(viewportId, opts = {}) {
  hideColorbar(viewportId);
  const vp = getRenderingEngine()?.getViewport(viewportId);
  const el = vp?.element;
  if (!el) return;

  const width = opts.width || 20;
  const minHU = opts.minHU || -1024;
  const maxHU = opts.maxHU || 3071;

  const canvas = document.createElement('canvas');
  canvas.style.cssText = `position:absolute;top:10px;right:10px;width:${width}px;height:60%;z-index:55;pointer-events:none;border:1px solid rgba(255,255,255,0.2);border-radius:4px;`;
  el.appendChild(canvas);

  const draw = () => {
    if (!el.isConnected) return;
    const h = canvas.parentElement.clientHeight * 0.6;
    canvas.width = width;
    canvas.height = h;
    const ctx = canvas.getContext('2d');

    // Градиент
    const grad = ctx.createLinearGradient(0, h, 0, 0);
    for (let i = 0; i <= 10; i++) {
      const t = i / 10;
      const hu = minHU + (maxHU - minHU) * t;
      const color = _huToColor(hu, minHU, maxHU);
      grad.addColorStop(t, color);
    }
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, width, h);

    // Метки
    ctx.fillStyle = '#fff';
    ctx.font = '9px monospace';
    ctx.textAlign = 'left';
    for (let i = 0; i <= 5; i++) {
      const y = (h / 5) * i;
      const hu = maxHU - (maxHU - minHU) * (i / 5);
      ctx.fillText(Math.round(hu).toString(), width + 4, y + 3);
    }
  };
  draw();
  const ro = new ResizeObserver(() => requestAnimationFrame(draw));
  ro.observe(el);
  _colorbars.set(viewportId, { canvas, ro, el });
}

function _huToColor(hu, min, max) {
  const t = (hu - min) / (max - min);
  if (t < 0.25) { const s = t / 0.25; return `rgb(${Math.round(s*50)},${Math.round(s*50)},${Math.round(128+s*127)})`; }
  if (t < 0.5) { const s = (t-0.25)/0.25; return `rgb(${Math.round(s*200)},${Math.round(s*200)},${Math.round(255-s*127)})`; }
  if (t < 0.75) { const s = (t-0.5)/0.25; return `rgb(${Math.round(50+s*205)},${Math.round(200+s*55)},${Math.round(128-s*128)})`; }
  const s = (t-0.75)/0.25; return `rgb(${Math.round(255-s*55)},${Math.round(255-s*55)},${Math.round(s*255)})`;
}

export function hideColorbar(viewportId) {
  const e = _colorbars.get(viewportId);
  if (!e) return;
  e.ro.disconnect();
  try { e.canvas.remove(); } catch {}
  _colorbars.delete(viewportId);
}

// ══════════════════════════════════════════════════════════════
//  RADIAL MENU (правая кнопка мыши)
// ══════════════════════════════════════════════════════════════

let _radialMenu = null;

const RADIAL_ACTIONS = [
  { label: '🔄 Сброс', action: (vpId) => _core().then(m => m.resetViewport(vpId)), angle: -90 },
  { label: '📸 Скрин', action: (vpId) => screenshotViewport(vpId), angle: -45 },
  { label: '🔍 Лупа', action: (vpId) => toggleMagnifier(vpId), angle: 0 },
  { label: '📏 Сетка', action: (vpId) => toggleRulerGrid(vpId), angle: 45 },
  { label: '🎚 Auto W/L', action: (vpId) => _core().then(m => m.autoWindowLevel?.(vpId)), angle: 90 },
  { label: '📌 Зам.', action: (vpId) => addBookmark(vpId), angle: 135 },
  { label: '✂ Справка', action: () => showToast('Меню быстрых действий', 'info', 1500), angle: 180 },
  { label: '❌ Закрыть', action: () => {}, angle: 225 },
];

export function showRadialMenu(viewportId, x, y) {
  hideRadialMenu();
  const vp = getRenderingEngine()?.getViewport(viewportId);
  const el = vp?.element || document.body;

  const container = document.createElement('div');
  container.className = 'vp-radial-menu';
  container.style.cssText = `position:fixed;left:${x - 120}px;top:${y - 120}px;width:240px;height:240px;z-index:9999;pointer-events:auto;`;

  const radius = 80;
  const cx = 120, cy = 120;

  for (const item of RADIAL_ACTIONS) {
    const angleRad = (item.angle * Math.PI) / 180;
    const ix = cx + Math.cos(angleRad) * radius - 20;
    const iy = cy + Math.sin(angleRad) * radius - 20;

    const btn = document.createElement('button');
    btn.textContent = item.label;
    btn.style.cssText = `position:absolute;left:${ix}px;top:${iy}px;width:40px;height:40px;border-radius:50%;background:rgba(0,0,0,0.8);border:1px solid rgba(255,255,255,0.3);color:#fff;font-size:16px;cursor:pointer;transition:0.1s;`;
    btn.onclick = (e) => {
      e.stopPropagation();
      hideRadialMenu();
      item.action(viewportId);
    };
    btn.onmouseenter = () => { btn.style.background = 'rgba(0,170,255,0.3)'; btn.style.borderColor = '#0af'; };
    btn.onmouseleave = () => { btn.style.background = 'rgba(0,0,0,0.8)'; btn.style.borderColor = 'rgba(255,255,255,0.3)'; };
    container.appendChild(btn);
  }

  // Центральная точка
  const center = document.createElement('div');
  center.style.cssText = `position:absolute;left:${cx-4}px;top:${cy-4}px;width:8px;height:8px;border-radius:50%;background:#0af;`;
  container.appendChild(center);

  // Клик вне меню закрывает
  const outsideHandler = (e) => {
    if (!container.contains(e.target)) {
      hideRadialMenu();
      document.removeEventListener('click', outsideHandler);
    }
  };
  setTimeout(() => document.addEventListener('click', outsideHandler), 10);

  el.appendChild(container);
  _radialMenu = { container, outsideHandler };
}

export function hideRadialMenu() {
  if (!_radialMenu) return;
  try { _radialMenu.container.remove(); } catch {}
  document.removeEventListener('click', _radialMenu.outsideHandler);
  _radialMenu = null;
}

// ══════════════════════════════════════════════════════════════
//  BOOKMARKS
// ══════════════════════════════════════════════════════════════

const _bookmarks = new Map();

export function addBookmark(viewportId, label, withThumb = false) {
  const vpId = viewportId || getActiveOrFirstViewportId();
  const vp = getRenderingEngine()?.getViewport(vpId);
  if (!vp) return null;
  const sliceIndex = vp.type === ViewportType.STACK ? (vp.getCurrentImageIdIndex?.() || 0) : vp.getCamera?.()?.focalPoint;
  const bm = {
    id: `bm-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    viewportId: vpId,
    sliceIndex,
    label: label || `Срез #${typeof sliceIndex === 'number' ? sliceIndex : '?'}`,
    timestamp: Date.now(),
    thumbnail: withThumb ? screenshotViewport(vpId, { overlay: false }) : null,
    cameraState: null,
  };
  try { const cam = vp.getCamera(); bm.cameraState = { position: cam.position ? [...cam.position] : null, focalPoint: cam.focalPoint ? [...cam.focalPoint] : null, viewUp: cam.viewUp ? [...cam.viewUp] : null, parallelScale: cam.parallelScale }; } catch (e) { log.debug("[viewportUI]", e.message); }
  if (!_bookmarks.has(vpId)) _bookmarks.set(vpId, []);
  _bookmarks.get(vpId).push(bm);
  events.emit(EVENTS.BOOKMARK_ADDED, bm);
  showToast(`📌 ${bm.label}`, 'info', 1500);
  return bm;
}

export function goToBookmark(bookmarkId) {
  for (const [vpId, marks] of _bookmarks) {
    const bm = marks.find(m => m.id === bookmarkId);
    if (!bm) continue;
    const vp = getRenderingEngine()?.getViewport(vpId);
    if (!vp) continue;
    if (vp.type === ViewportType.STACK && typeof bm.sliceIndex === 'number') vp.setImageIdIndex(bm.sliceIndex);
    else if (bm.cameraState) vp.setCamera(bm.cameraState);
    vp.render();
    showToast(`📌 → ${bm.label}`, 'info', 1000);
    return;
  }
}

export function removeBookmark(bookmarkId) {
  for (const [vpId, marks] of _bookmarks) {
    const idx = marks.findIndex(m => m.id === bookmarkId);
    if (idx >= 0) { marks.splice(idx, 1); events.emit(EVENTS.BOOKMARK_REMOVED, { id: bookmarkId, viewportId: vpId }); return; }
  }
}

export function getBookmarks(viewportId) {
  if (viewportId) return [...(_bookmarks.get(viewportId) || [])];
  const all = [];
  for (const m of _bookmarks.values()) all.push(...m);
  return all.sort((a, b) => a.timestamp - b.timestamp);
}

export function navigateBookmarks(viewportId, direction = 'next') {
  const marks = _bookmarks.get(viewportId);
  if (!marks?.length) return;
  const vp = getRenderingEngine()?.getViewport(viewportId);
  const cur = vp?.type === ViewportType.STACK ? (vp.getCurrentImageIdIndex?.() || 0) : null;
  const bm = direction === 'next'
    ? marks.find(m => typeof m.sliceIndex === 'number' && m.sliceIndex > cur) || marks[0]
    : [...marks].reverse().find(m => typeof m.sliceIndex === 'number' && m.sliceIndex < cur) || marks[marks.length - 1];
  if (bm) goToBookmark(bm.id);
}

// ══════════════════════════════════════════════════════════════
//  GESTURES (кэшированный _core)
// ══════════════════════════════════════════════════════════════

const _gestureTargets = new Map();

export function enableGestures(viewportId) {
  const vp = getRenderingEngine()?.getViewport(viewportId);
  const el = vp?.element;
  if (!el || _gestureTargets.has(viewportId)) return;

  const st = { lastTap: 0, lastPinchDist: 0, swipeStart: null };

  const onTouchStart = (e) => {
    if (e.touches.length === 1) {
      const now = Date.now();
      if (now - st.lastTap < 300) { _core().then(m => m.resetViewport(viewportId)); showToast('🔄 Сброс', 'info', 1000); st.lastTap = 0; }
      else st.lastTap = now;
      st.swipeStart = { x: e.touches[0].clientX, y: e.touches[0].clientY, time: now };
    }
    if (e.touches.length === 2) st.lastPinchDist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2) {
      const dist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
      if (st.lastPinchDist > 0 && Math.abs(dist / st.lastPinchDist - 1) > 0.02) _core().then(m => m.zoomViewport(viewportId, dist > st.lastPinchDist ? 'in' : 'out'));
      st.lastPinchDist = dist;
    }
  };
  const onTouchEnd = (e) => {
    if (e.changedTouches.length === 1 && st.swipeStart) {
      const t = e.changedTouches[0], dy = t.clientY - st.swipeStart.y;
      if (Math.abs(dy) > 50 && Math.abs(dy) > Math.abs(t.clientX - st.swipeStart.x)) {
        try { if (vp.scroll) for (let i = 0; i < Math.min(Math.ceil(Math.abs(dy) / 30), 10); i++) vp.scroll(dy < 0 ? 1 : -1); } catch (e) { log.debug("[viewportUI]", e.message); }
      }
      st.swipeStart = null;
    }
    if (!e.touches.length) st.lastPinchDist = 0;
  };

  el.addEventListener('touchstart', onTouchStart, { passive: true });
  el.addEventListener('touchmove', onTouchMove, { passive: true });
  el.addEventListener('touchend', onTouchEnd, { passive: true });
  _gestureTargets.set(viewportId, { el, listeners: [['touchstart', onTouchStart], ['touchmove', onTouchMove], ['touchend', onTouchEnd]] });
}

export function disableGestures(viewportId) {
  const e = _gestureTargets.get(viewportId);
  if (!e) return;
  for (const [evt, h] of e.listeners) e.el.removeEventListener(evt, h);
  _gestureTargets.delete(viewportId);
}

export function enableAllGestures() { for (const vpId of getViewportIds()) enableGestures(vpId); }

// ══════════════════════════════════════════════════════════════
//  CLEANUP
// ══════════════════════════════════════════════════════════════

export function destroyAllUI() {
  for (const vpId of _magnifiers.keys()) disableMagnifier(vpId);
  for (const vpId of _crosshairs.keys()) hideCrosshairOverlay(vpId);
  for (const vpId of _rulerGrids.keys()) removeRulerGrid(vpId);
  for (const vpId of _infoOverlays.keys()) hideInfoOverlay(vpId);
  for (const vpId of _pipOverlays.keys()) hidePictureInPicture(vpId);
  for (const vpId of _letterboxes.keys()) hideLetterbox(vpId);
  for (const vpId of _progressIndicators.keys()) hideProgressIndicator(vpId);
  for (const vpId of _tooltips.keys()) disableMeasurementTooltips(vpId);
  for (const vpId of _colorbars.keys()) hideColorbar(vpId);
  for (const vpId of _gestureTargets.keys()) disableGestures(vpId);
  hideRadialMenu();
}